// PWA 安装能力：统一管理 Chrome/Edge 安装事件、安装状态与 Service Worker 注册。
import { readonly, shallowRef } from "vue";

// Chromium 安装确认结果。
type PwaInstallChoice = {
  // 用户对浏览器安装弹窗的选择。
  outcome: "accepted" | "dismissed";
  // 执行安装的平台标识，由浏览器提供。
  platform: string;
};

// Chromium 提供的非标准 PWA 安装事件，TypeScript DOM 类型暂未内置。
interface BeforeInstallPromptEvent extends Event {
  // 用户对安装弹窗的最终选择。
  readonly userChoice: Promise<PwaInstallChoice>;
  /**
   * 显示由浏览器控制的 PWA 安装确认弹窗。
   * @returns 弹窗显示完成后的 Promise
   */
  prompt(): Promise<void>;
}

// 顶栏根据结果给出对应反馈，避免组件依赖浏览器事件细节。
export type PwaInstallResult =
  | "accepted"
  | "dismissed"
  | "installed"
  | "unavailable";

// 当前页面生命周期内可使用的浏览器安装事件；每个事件只能调用一次。
const deferredInstallPrompt = shallowRef<BeforeInstallPromptEvent | null>(null);
// 独立窗口运行表示应用已经安装并从桌面入口启动。
const isInstalled = shallowRef(false);
// 全局监听器只初始化一次，避免布局重复挂载时叠加事件。
let isInitialized = false;

/**
 * 判断当前页面是否正以已安装应用的独立窗口模式运行。
 * @returns 是否处于 standalone 模式
 */
function detectStandaloneMode(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches;
}

/**
 * 保存浏览器提供的安装事件，交由顶栏按钮在用户点击时触发。
 * @param event Chromium 的 PWA 安装事件
 */
function handleBeforeInstallPrompt(event: Event): void {
  event.preventDefault();
  deferredInstallPrompt.value = event as BeforeInstallPromptEvent;
}

/**
 * 浏览器完成安装后同步状态，避免继续显示重复安装入口。
 */
function handleAppInstalled(): void {
  deferredInstallPrompt.value = null;
  isInstalled.value = true;
}

/**
 * 注册构建阶段生成的 Service Worker；注册失败不阻断平台正常使用。
 */
async function registerServiceWorker(): Promise<void> {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;

  try {
    // Hash 路由不会参与 URL 解析，可兼容根目录及子目录部署。
    const serviceWorkerUrl = new URL(
      "./service-worker.js",
      window.location.href
    );
    await navigator.serviceWorker.register(serviceWorkerUrl, { scope: "./" });
  } catch (error) {
    // 安装能力应渐进增强，缓存注册异常只记录诊断信息。
    console.warn("[PWA] Service Worker 注册失败", error);
  }
}

/**
 * 在应用启动早期初始化安装监听，避免登录后进入 Office 布局时错过安装事件。
 */
export function initializePwaInstall(): void {
  if (isInitialized || typeof window === "undefined") return;

  isInitialized = true;
  isInstalled.value = detectStandaloneMode();
  window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  window.addEventListener("appinstalled", handleAppInstalled);
  void registerServiceWorker();
}

/**
 * 提供给界面的只读安装状态与用户触发动作。
 * @returns PWA 是否可安装、是否已安装及安装方法
 */
export function usePwaInstall() {
  /**
   * 响应用户点击并唤起 Chrome/Edge 的安装确认框。
   * @returns 用户选择或当前不可安装状态
   */
  async function installPwa(): Promise<PwaInstallResult> {
    if (isInstalled.value) return "installed";

    // 未收到事件通常表示浏览器不支持、条件未满足或应用已被其他入口安装。
    const installPrompt = deferredInstallPrompt.value;
    if (!installPrompt) return "unavailable";

    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      deferredInstallPrompt.value = null;
      if (choice.outcome === "accepted") isInstalled.value = true;
      return choice.outcome;
    } catch (error) {
      // 浏览器拒绝重复或失效的安装事件时，恢复为不可安装状态并允许用户稍后重试。
      deferredInstallPrompt.value = null;
      console.warn("[PWA] 安装弹窗打开失败", error);
      return "unavailable";
    }
  }

  return {
    // 浏览器已判定当前 PWA 满足安装条件。
    canInstall: readonly(deferredInstallPrompt),
    // 当前页面是否运行在已安装应用窗口中。
    isInstalled: readonly(isInstalled),
    installPwa,
  };
}
